import { useCreate, useDelete, useGetList, useGetOne, useUpdate } from '@queries/request'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { generatePath } from 'react-router-dom'
import { format } from 'date-fns'

import api from '@api/api'

import { OrgStructureKeys, ReportKeys } from '@queries/Keys'
import { parseBlobErrorResponse, readBlobText } from '@helpers/ErrorHandler/blobError'
import type { Report, Filter, ReportPayload, ExportParams } from '@models/Report'

const PATHS = {
  reports: '/api/reports/',
  report: '/api/reports/:id',
  export: '/api/reports/export',
}

export const useGetReports = (filter?: Filter, enabled = true) => {
  return useGetList<Report>([ReportKeys.Reports], PATHS.reports, {
    query: {
      enabled,
    },
    axios: {
      params: { ...(filter || {}) },
    },
  })
}

export const useGetReport = (id?: string) => {
  return useGetOne<Report>([ReportKeys.Report, id || ''], generatePath(PATHS.report, { id: id }), {
    query: {
      enabled: !!id,
      suspense: true,
    },
  })
}

export const useCreateReport = () => {
  return useCreate<ReportPayload, Report>(PATHS.reports)
}

export const useDeleteReport = () => {
  const cache = useQueryClient()
  return useDelete<number>((id) => generatePath(PATHS.report, { id }), {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([ReportKeys.Reports])
      },
    },
  })
}

export const useEditReport = (id?: string) => {
  const cache = useQueryClient()
  return useUpdate<Partial<ReportPayload>, Report>(generatePath(PATHS.report, { id: id || '' }), {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([ReportKeys.Report, id])
        cache.invalidateQueries([ReportKeys.Reports])
      },
    },
  })
}

export const useGenerateReport = (reportName: string, reportType?: string) => {
  return useMutation(
    async (params: ExportParams) => {
      const path = reportType ? `${PATHS.export}/${reportType}` : PATHS.export
      try {
        const response = await api.get<Blob>(path, {
          params: Object.keys(params).reduce(
            (acc, key) =>
              params[key as keyof ExportParams] ? { ...acc, [key]: params[key as keyof ExportParams] } : acc,
            {},
          ),
          responseType: 'blob',
        })
        if (response.status === 204) {
          return response
        }
        if (response.status === 202) {
          // Large report: the backend generates it in the background and emails a
          // download link (AABT-57). Do NOT trigger a file download. The JSON body
          // arrives as a Blob because responseType is 'blob', so read it back.
          let detail = ''
          try {
            // Env-robust blob read (Blob.text() in browsers, FileReader fallback
            // for webviews / jsdom) — same reader the error path uses.
            const parsed: unknown = JSON.parse(await readBlobText(response.data))
            if (
              parsed &&
              typeof parsed === 'object' &&
              'detail' in parsed &&
              typeof (parsed as { detail: unknown }).detail === 'string'
            ) {
              detail = (parsed as { detail: string }).detail
            }
          } catch {
            /* fall back to the translated message in the popup */
          }
          return { status: 202 as const, async: true as const, detail }
        }
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          `${reportName || reportType || 'report'}_${format(new Date(), 'yyyy-MM-dd-hh-mm')}.${params.type}`,
        )
        document.body.appendChild(link)
        link.click()

        return null
      } catch (error) {
        // responseType 'blob' wraps a JSON error body in a Blob (type
        // application/json), which the error handler can't read — it would show
        // "type: application/json" instead of the real backend message (e.g. the
        // export-already-running 429). Convert it back before rethrowing. AABT-37.
        await parseBlobErrorResponse(error)
        throw error
      }
    },
    {
      mutationKey: [OrgStructureKeys.Export],
    },
  )
}
